import { Component, DoCheck, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import L from 'leaflet';
import _ from 'lodash';
import {
    FeatureMapLayersService,
    SymbologyProperties
} from 'src/app/feature/map-viewer/map-container/feature-map-layers/feature-map-layers.service';
import { MapService } from 'src/app/feature/map-viewer/map.service';
import { GeometryTypes } from 'src/app/shared/common/utility/geometry-utils';
import { Feature, IFeatureGeometry } from 'src/app/shared/map-data-services/feature/feature';
import { LayerStyleGenerator } from 'src/app/shared/map-data-services/layer/layer-style-generator';
import { Style } from 'src/app/shared/map-data-services/styles/style';
import { LayerSymbologyType } from 'src/app/shared/map-data-services/styles/style-symbology';
import { Template } from 'src/app/shared/template-services/template';

@Component({
    selector: 'symbology-preview',
    templateUrl: './symbology-preview.component.html'
})
export class SymbologyPreviewComponent implements OnInit, DoCheck {
    @Input()
    private geometryType: GeometryTypes;

    @Input()
    private templateStyle: Style;

    @Input()
    private template: Template;

    @ViewChild('previewMap', { static: true })
    mapElement: ElementRef;

    private symbologyProperties: SymbologyProperties;

    private templateStylePrevious: Style;
    private templatePrevious: Template;
    private geometryTypePrevious: GeometryTypes;

    private map: L.Map = null;
    private layerGroup: L.LayerGroup = null;

    constructor(private mapService: MapService, private featureMapLayers: FeatureMapLayersService) {
        this.checkChanges = _.throttle(this.checkChanges, 200);
    }

    ngOnInit(): void {
        this.templateStylePrevious = _.cloneDeep(this.templateStyle);
        this.templatePrevious = _.cloneDeep(this.template);
        this.geometryTypePrevious = this.geometryType;

        this.initializeSymbologyProperties();

        this.initializeMap();

        this.updateMap();
    }

    ngDoCheck(): void {
        this.checkChanges();
    }

    private checkChanges(): void {
        if (
            !_.isEqual(this.templateStylePrevious, this.templateStyle) &&
            _.isEqual(this.templateStylePrevious.styleOverrides, this.templateStyle.styleOverrides)
        ) {
            // Trigger update only if the style properties have changed, not the style overrides.
            this.initializeSymbologyProperties();
            this.templateStylePrevious = _.cloneDeep(this.templateStyle);
            this.updateMap();
        }
        if (!_.isEqual(this.templatePrevious, this.template)) {
            this.initializeSymbologyProperties();
            this.templatePrevious = _.cloneDeep(this.template);
            this.updateMap();
        }
        if (this.geometryTypePrevious !== this.geometryType) {
            this.geometryTypePrevious = this.geometryType;
            this.updateMap();
        }
    }

    private initializeSymbologyProperties(): void {
        this.symbologyProperties = {
            layerId: 'custom-symbol-preview',
            geometryType: this.geometryType,
            colorKey: LayerStyleGenerator.decimalToHexRgb(this.templateStyle.symbol.fillColor, true),
            symbolId: this.templateStyle.symbol.symbolDefinitionUuid,
            symbolName: this.templateStyle.symbol.symbolName,
            symbolFillColor: LayerStyleGenerator.decimalToHexRgb(this.templateStyle.symbol.fillColor),
            symbolOutlineColor:
                this.geometryType === GeometryTypes.LINE
                    ? LayerStyleGenerator.decimalToHexRgb(this.templateStyle.lineColor)
                    : LayerStyleGenerator.decimalToHexRgb(this.templateStyle.symbol.outlineColor),
            symbolText: this.templateStyle.symbol.text,
            symbolTextColor: LayerStyleGenerator.decimalToHexRgb(this.templateStyle.symbol.color),

            // line, polygon properties
            lineStyle: this.templateStyle.lineStyle,
            lineColor: LayerStyleGenerator.decimalToHexRgb(this.templateStyle.lineColor),
            fillColor: LayerStyleGenerator.decimalToHexRgb(this.templateStyle.fillColor),
            fillOpacity: this.templateStyle.fillOpacity,

            active: false,
            selected: false,
            inVisibleTask: false,
            updated: false
        };

        if (this.templateStyle.symbol.type === LayerSymbologyType.CUSTOMIZABLE) {
            this.symbologyProperties.symbolOutlineColor = this.symbologyProperties.symbolTextColor;
        }
    }

    private initializeMap(): void {
        const map = this.mapService.intializeMap(this.mapElement.nativeElement, '', '', {
            attributionControl: false,
            maxZoom: 16,
            dragging: false,
            touchZoom: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            boxZoom: false
        });
        this.map = map;

        this.layerGroup = L.layerGroup().addTo(this.map);

        const resizeObserver = new ResizeObserver(() => {
            this.map.invalidateSize();
        });
        resizeObserver.observe(this.mapElement.nativeElement);
    }

    private updateMap(): void {
        this.layerGroup.clearLayers();

        // Line symbol markers are not in layerGroup, they are added directly to the L.map in 'this'.
        this.map.eachLayer((layer: L.Layer) => {
            if (layer !== this.layerGroup) {
                this.map.removeLayer(layer);
            }
        });

        if (this.geometryType === GeometryTypes.POINT) {
            this.drawPreviewPoint();
        } else if (this.geometryType === GeometryTypes.LINE) {
            this.drawPreviewLine();
        } else if (this.geometryType === GeometryTypes.AREA) {
            this.drawPreviewPolygon();
        }
    }

    private drawPreviewPoint(): void {
        const markerOptions: L.MarkerOptions = this.featureMapLayers.createCustomSymbolMarker(
            ['active'],
            this.symbologyProperties
        );
        const markerPosition: L.LatLngExpression = [51.505, -0.09];
        const markerBounds = L.latLngBounds([markerPosition]);

        L.marker([51.505, -0.09], markerOptions).addTo(this.layerGroup);

        this.map.fitBounds(markerBounds);
    }

    private drawPreviewLine(): void {
        const lineCoordinates: L.LatLngExpression[] = [
            [51.750499, -0.666698],
            [51.7539, -0.562258],
            [51.713076, -0.254436],
            [51.788085, 0.04789],
            [51.788085, 0.16882]
        ];

        const pathOptions: L.PathOptions = this.featureMapLayers.getPolylineStyleOptions(this.symbologyProperties);

        const polyline = L.polyline(lineCoordinates, pathOptions).addTo(this.layerGroup);

        const bounds = polyline.getBounds();
        const center = bounds.getCenter();

        this.map.setView(center, 9);

        if (this.template.enableSymbols) {
            // Convert L.LatLngExpression[] to [number, number][]
            const coordinates: [number, number][] = lineCoordinates.map((latlng: L.LatLngExpression) => {
                const latlngObj = L.latLng(latlng);
                return [latlngObj.lng, latlngObj.lat];
            });

            const feature: Feature = {
                id: 'custom-symbol-preview',
                layerId: 'custom-symbol-preview',
                geometryType: GeometryTypes.LINE_STRING,
                geometry: {
                    type: GeometryTypes.GEOMETRY_COLLECTION,
                    geometries: [
                        {
                            type: GeometryTypes.LINE_STRING,
                            coordinates: coordinates
                        }
                    ]
                } as IFeatureGeometry,
                style: {
                    symbol: this.templateStyle.symbol
                }
            } as Feature;

            const markerOptions: L.MarkerOptions = this.featureMapLayers.createCustomSymbolMarker(
                ['active', 'style-panel-preview'],
                this.symbologyProperties
            );
            this.featureMapLayers.layerSymbologyMarkerIconsMap.set(feature.layerId, markerOptions);

            this.featureMapLayers.onCustomSymbologyFeature(feature, this, 20000, true);
        }
    }

    private drawPreviewPolygon(): void {
        const polygonCoordinates: L.LatLngExpression[] = [
            [51.757, -0.685],
            [51.764, -0.65],
            [51.77, -0.563],
            [51.757, -0.535],
            [51.742, -0.563],
            [51.754, -0.598],
            [51.745, -0.605],
            [51.74, -0.67]
        ];

        const pathOptions: L.PolylineOptions = this.featureMapLayers.getPolygonStyleOptions(this.symbologyProperties);

        const polygon = L.polygon(polygonCoordinates, pathOptions).addTo(this.layerGroup);

        const bounds = polygon.getBounds();

        this.map.setView(bounds.getCenter(), 11);
    }
}
